import * as React from 'react';

import {
  type FileUploadAccept,
  type FileUploadAcceptedMeta,
  type FileUploadRejection,
} from './file-upload.types';
import { isEditablePasteTarget } from './is-editable-paste-target';
import { normalizeAccept } from './normalize-accept';
import { validatePastedFiles } from './validate-pasted-files';

export interface UseDocumentFilePasteOptions {
  /**
   * Attach the document-level paste listener while true. Flipping to false
   * removes it, so the feature owning the page can hand paste ownership between
   * surfaces (e.g. disable while a modal with its own paste target is open).
   */
  enabled: boolean;
  /** MIME/extension filter — same shape as the file picker's `accept`. */
  accept?: FileUploadAccept;
  /** @default false */
  multiple?: boolean;
  /** Hard cap on files per paste (0 = unlimited). @default 0 */
  maxFiles?: number;
  /** Maximum size per file in bytes. */
  maxSize?: number;
  /** Minimum size per file in bytes. */
  minSize?: number;
  onFilesAccepted?: (files: File[], meta: FileUploadAcceptedMeta) => void;
  onFilesRejected?: (rejections: FileUploadRejection[]) => void;
  /**
   * When provided, a paste whose target is an editable element (input,
   * textarea, contenteditable) is still handled if that element lives inside
   * this container. Without a container ref every editable target is skipped so
   * typing a paste into a form field never triggers a file upload.
   */
  containerRef?: React.RefObject<HTMLElement>;
}

/**
 * Handles files pasted anywhere on the page and routes them through the shared
 * file-upload validation, guarding editable targets so a clipboard paste into a
 * text field is never mistaken for a file drop. Extracted from `FileDropzone`
 * so a surface can own document-wide paste without rendering a dropzone.
 */
export function useDocumentFilePaste({
  enabled,
  accept,
  multiple = false,
  maxFiles = 0,
  maxSize,
  minSize,
  onFilesAccepted,
  onFilesRejected,
  containerRef,
}: UseDocumentFilePasteOptions): void {
  const normalizedAccept = React.useMemo(
    () => (accept != null ? normalizeAccept(accept) : undefined),
    [accept]
  );

  const handlePaste = React.useCallback(
    (event: ClipboardEvent) => {
      if (event.defaultPrevented) return;
      const pastedFiles = Array.from(event.clipboardData?.files ?? []);
      if (pastedFiles.length === 0) return;
      event.preventDefault();
      const { accepted, rejected } = validatePastedFiles(pastedFiles, {
        accept: normalizedAccept,
        multiple,
        maxFiles,
        maxSize,
        minSize,
      });
      if (accepted.length > 0) onFilesAccepted?.(accepted, { method: 'paste' });
      if (rejected.length > 0) onFilesRejected?.(rejected);
    },
    [
      normalizedAccept,
      multiple,
      maxFiles,
      maxSize,
      minSize,
      onFilesAccepted,
      onFilesRejected,
    ]
  );

  React.useEffect(() => {
    if (!enabled) return;

    const handleDocumentPaste = (event: ClipboardEvent) => {
      if (
        isEditablePasteTarget(event.target) &&
        !containerRef?.current?.contains(event.target)
      ) {
        return;
      }
      handlePaste(event);
    };
    document.addEventListener('paste', handleDocumentPaste);
    return () => document.removeEventListener('paste', handleDocumentPaste);
  }, [enabled, handlePaste, containerRef]);
}
